// Comprehensive filter options for apartment search
// Covers Japanese real estate portal requirements

export interface FilterOption {
  value: string;
  labelKey: string;
}

export interface FilterCategory {
  id: string;
  labelKey: string;
  icon: string;
  filters: FilterConfig[];
}

export interface FilterConfig {
  id: string;
  labelKey: string;
  type: "checkbox" | "select" | "multiselect" | "range" | "radio" | "dualRange";
  options?: FilterOption[];
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  minId?: string;
  maxId?: string;
  formatValue?: "currency" | "area" | "minutes";
}

// Property Types
export const PROPERTY_TYPES: FilterOption[] = [
  { value: "apartment", labelKey: "filterPropertyApartment" },
  { value: "mansion", labelKey: "filterPropertyMansion" },
  { value: "share_house", labelKey: "filterPropertyShareHouse" },
  { value: "guest_house", labelKey: "filterPropertyGuestHouse" },
  { value: "monthly_mansion", labelKey: "filterPropertyMonthlyMansion" },
];

// Room layouts (Japanese style)
export const ROOM_LAYOUTS: FilterOption[] = [
  { value: "1R", labelKey: "filterLayout1R" },
  { value: "1K", labelKey: "filterLayout1K" },
  { value: "1DK", labelKey: "filterLayout1DK" },
  { value: "1LDK", labelKey: "filterLayout1LDK" },
  { value: "2K", labelKey: "filterLayout2K" },
  { value: "2DK", labelKey: "filterLayout2DK" },
  { value: "2LDK", labelKey: "filterLayout2LDK" },
  { value: "3K", labelKey: "filterLayout3K" },
  { value: "3DK", labelKey: "filterLayout3DK" },
  { value: "3LDK+", labelKey: "filterLayout3LDKPlus" },
];

// Key money options
export const KEY_MONEY_OPTIONS: FilterOption[] = [
  { value: "0", labelKey: "filterNoKeyMoney" },
  { value: "0.5", labelKey: "filterHalfMonth" },
  { value: "1", labelKey: "filter1Month" },
  { value: "any", labelKey: "filterAny" },
];

// Deposit options
export const DEPOSIT_OPTIONS: FilterOption[] = [
  { value: "0", labelKey: "filterNoDeposit" },
  { value: "1", labelKey: "filter1Month" },
  { value: "2", labelKey: "filter2Months" },
  { value: "any", labelKey: "filterAny" },
];

// Building age options
export const BUILDING_AGE_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterAnyAge" },
  { value: "5", labelKey: "filterUnder5Years" },
  { value: "10", labelKey: "filterUnder10Years" },
  { value: "20", labelKey: "filterUnder20Years" },
  { value: "30", labelKey: "filterUnder30Years" },
];

// Floor preferences
export const FLOOR_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterAnyFloor" },
  { value: "1", labelKey: "filterFirstFloor" },
  { value: "2+", labelKey: "filter2ndFloorPlus" },
  { value: "3+", labelKey: "filter3rdFloorPlus" },
  { value: "top", labelKey: "filterTopFloor" },
];

// Walk to station options
export const WALK_TO_STATION_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterAnyWalkTime" },
  { value: "5", labelKey: "filterWithin5Min" },
  { value: "10", labelKey: "filterWithin10Min" },
  { value: "15", labelKey: "filterWithin15Min" },
  { value: "20", labelKey: "filterWithin20Min" },
];

// Building structure options (multiselect)
export const BUILDING_STRUCTURE_OPTIONS: FilterOption[] = [
  { value: "rc", labelKey: "filterStructureRC" },
  { value: "steel", labelKey: "filterStructureSteel" },
  { value: "wood", labelKey: "filterStructureWood" },
  { value: "src", labelKey: "filterStructureSRC" },
];

// Listing freshness options
export const LISTING_FRESHNESS_OPTIONS: FilterOption[] = [
  { value: "any", labelKey: "filterFreshnessAny" },
  { value: "24h", labelKey: "filterFreshness24h" },
  { value: "3d", labelKey: "filterFreshness3d" },
  { value: "7d", labelKey: "filterFreshness7d" },
  { value: "14d", labelKey: "filterFreshness14d" },
];

// All filter categories - reorganized
export const FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: "property",
    labelKey: "filterCategoryProperty",
    icon: "Home",
    filters: [
      {
        id: "propertyType",
        labelKey: "filterPropertyType",
        type: "multiselect",
        options: PROPERTY_TYPES,
      },
    ],
  },
  {
    id: "price",
    labelKey: "filterCategoryPrice",
    icon: "Banknote",
    filters: [
      {
        id: "rent",
        labelKey: "filterMinRent",
        type: "dualRange",
        minId: "rentMin",
        maxId: "rentMax",
        min: 0,
        max: 500000,
        step: 10000,
        unit: "¥",
        formatValue: "currency",
      },
      {
        id: "includeManagementFee",
        labelKey: "filterIncludeManagementFee",
        type: "checkbox",
      },
      {
        id: "keyMoney",
        labelKey: "filterKeyMoney",
        type: "select",
        options: KEY_MONEY_OPTIONS,
      },
      {
        id: "deposit",
        labelKey: "filterDeposit",
        type: "select",
        options: DEPOSIT_OPTIONS,
      },
      {
        id: "guarantorRequired",
        labelKey: "filterGuarantor",
        type: "radio",
        options: [
          { value: "any", labelKey: "filterAny" },
          { value: "yes", labelKey: "filterYes" },
          { value: "no", labelKey: "filterNo" },
        ],
      },
    ],
  },
  {
    id: "size",
    labelKey: "filterCategorySize",
    icon: "LayoutGrid",
    filters: [
      {
        id: "roomLayout",
        labelKey: "filterRoomLayout",
        type: "multiselect",
        options: ROOM_LAYOUTS,
      },
      {
        id: "size",
        labelKey: "filterMinSize",
        type: "dualRange",
        minId: "sizeMin",
        maxId: "sizeMax",
        min: 0,
        max: 200,
        step: 5,
        unit: "m²",
        formatValue: "area",
      },
    ],
  },
  {
    id: "bathroom",
    labelKey: "filterCategoryBathroom",
    icon: "Bath",
    filters: [
      {
        id: "bathToiletSeparate",
        labelKey: "filterBathToiletSeparate",
        type: "checkbox",
      },
      {
        id: "reheatingBath",
        labelKey: "filterReheatingBath",
        type: "checkbox",
      },
      {
        id: "bathroomDryer",
        labelKey: "filterBathroomDryer",
        type: "checkbox",
      },
      { id: "washletSeat", labelKey: "filterWashletSeat", type: "checkbox" },
      {
        id: "independentVanity",
        labelKey: "filterIndependentVanity",
        type: "checkbox",
      },
      {
        id: "indoorWasherSpace",
        labelKey: "filterIndoorWasherSpace",
        type: "checkbox",
      },
      { id: "washerHookup", labelKey: "filterWasherHookup", type: "checkbox" },
      {
        id: "washerDryerInUnit",
        labelKey: "filterWasherDryerInUnit",
        type: "checkbox",
      },
    ],
  },
  {
    id: "kitchen",
    labelKey: "filterCategoryKitchen",
    icon: "ChefHat",
    filters: [
      {
        id: "systemKitchen",
        labelKey: "filterSystemKitchen",
        type: "checkbox",
      },
      {
        id: "counterKitchen",
        labelKey: "filterCounterKitchen",
        type: "checkbox",
      },
      { id: "gasStove", labelKey: "filterGasStove", type: "checkbox" },
      { id: "ihCooktop", labelKey: "filterIHCooktop", type: "checkbox" },
      { id: "dishwasher", labelKey: "filterDishwasher", type: "checkbox" },
    ],
  },
  {
    id: "building",
    labelKey: "filterCategoryBuilding",
    icon: "Building2",
    filters: [
      {
        id: "buildingAge",
        labelKey: "filterBuildingAge",
        type: "select",
        options: BUILDING_AGE_OPTIONS,
      },
      {
        id: "floor",
        labelKey: "filterFloor",
        type: "select",
        options: FLOOR_OPTIONS,
      },
      {
        id: "buildingStructure",
        labelKey: "filterBuildingStructure",
        type: "multiselect",
        options: BUILDING_STRUCTURE_OPTIONS,
      },
      { id: "elevator", labelKey: "filterElevator", type: "checkbox" },
      { id: "southFacing", labelKey: "filterSouthFacing", type: "checkbox" },
      { id: "cornerUnit", labelKey: "filterCornerUnit", type: "checkbox" },
      { id: "balcony", labelKey: "filterBalcony", type: "checkbox" },
      { id: "flooringWood", labelKey: "filterFlooringWood", type: "checkbox" },
    ],
  },
  {
    id: "security",
    labelKey: "filterCategorySecurity",
    icon: "Shield",
    filters: [
      { id: "autoLock", labelKey: "filterAutoLock", type: "checkbox" },
      {
        id: "securityCamera",
        labelKey: "filterSecurityCamera",
        type: "checkbox",
      },
      {
        id: "videoIntercom",
        labelKey: "filterVideoIntercom",
        type: "checkbox",
      },
      { id: "deliveryBox", labelKey: "filterDeliveryBox", type: "checkbox" },
    ],
  },
  {
    id: "amenities",
    labelKey: "filterCategoryAmenities",
    icon: "Star",
    filters: [
      {
        id: "airConditioning",
        labelKey: "filterAirConditioning",
        type: "checkbox",
      },
      {
        id: "internetIncluded",
        labelKey: "filterInternetIncluded",
        type: "checkbox",
      },
      { id: "parking", labelKey: "filterParking", type: "checkbox" },
      {
        id: "bicycleParking",
        labelKey: "filterBicycleParking",
        type: "checkbox",
      },
      {
        id: "motorcycleParking",
        labelKey: "filterMotorcycleParking",
        type: "checkbox",
      },
    ],
  },
  {
    id: "access",
    labelKey: "filterCategoryAccess",
    icon: "Train",
    filters: [
      {
        id: "walkToStation",
        labelKey: "filterWalkToStation",
        type: "select",
        options: WALK_TO_STATION_OPTIONS,
      },
    ],
  },
  {
    id: "contract",
    labelKey: "filterCategoryContract",
    icon: "FileText",
    filters: [
      {
        id: "foreignerFriendly",
        labelKey: "filterForeignerFriendly",
        type: "checkbox",
      },
      { id: "petsAllowed", labelKey: "filterPetsAllowed", type: "checkbox" },
      {
        id: "instrumentsOK",
        labelKey: "filterInstrumentsOK",
        type: "checkbox",
      },
      { id: "sohoOK", labelKey: "filterSohoOK", type: "checkbox" },
      {
        id: "noGuarantorNeeded",
        labelKey: "filterNoGuarantorNeeded",
        type: "checkbox",
      },
      {
        id: "moveInWithin30Days",
        labelKey: "filterMoveInWithin30Days",
        type: "checkbox",
      },
    ],
  },
  {
    id: "listing",
    labelKey: "filterCategoryListing",
    icon: "Clock",
    filters: [
      {
        id: "listingFreshness",
        labelKey: "filterListingFreshness",
        type: "select",
        options: LISTING_FRESHNESS_OPTIONS,
      },
    ],
  },
];

// Default filter state
export interface FilterState {
  propertyType: string[];
  rentMin: number;
  rentMax: number;
  includeManagementFee: boolean;
  keyMoney: string;
  deposit: string;
  guarantorRequired: string;
  roomLayout: string[];
  sizeMin: number;
  sizeMax: number;
  // Bathroom & laundry
  bathToiletSeparate: boolean;
  reheatingBath: boolean;
  bathroomDryer: boolean;
  washletSeat: boolean;
  independentVanity: boolean;
  indoorWasherSpace: boolean;
  washerHookup: boolean;
  washerDryerInUnit: boolean;
  // Kitchen
  systemKitchen: boolean;
  counterKitchen: boolean;
  gasStove: boolean;
  ihCooktop: boolean;
  dishwasher: boolean;
  // Building
  buildingAge: string;
  floor: string;
  buildingStructure: string[];
  elevator: boolean;
  southFacing: boolean;
  cornerUnit: boolean;
  balcony: boolean;
  flooringWood: boolean;
  // Security
  autoLock: boolean;
  securityCamera: boolean;
  videoIntercom: boolean;
  deliveryBox: boolean;
  // Amenities
  airConditioning: boolean;
  internetIncluded: boolean;
  parking: boolean;
  bicycleParking: boolean;
  motorcycleParking: boolean;
  // Access
  walkToStation: string;
  // Contract
  foreignerFriendly: boolean;
  petsAllowed: boolean;
  instrumentsOK: boolean;
  sohoOK: boolean;
  noGuarantorNeeded: boolean;
  moveInWithin30Days: boolean;
  // Listing
  listingFreshness: string;
}

export const DEFAULT_FILTER_STATE: FilterState = {
  propertyType: [],
  rentMin: 0,
  rentMax: 500000,
  includeManagementFee: true,
  keyMoney: "any",
  deposit: "any",
  guarantorRequired: "any",
  roomLayout: [],
  sizeMin: 0,
  sizeMax: 200,
  // Bathroom & laundry
  bathToiletSeparate: false,
  reheatingBath: false,
  bathroomDryer: false,
  washletSeat: false,
  independentVanity: false,
  indoorWasherSpace: false,
  washerHookup: false,
  washerDryerInUnit: false,
  // Kitchen
  systemKitchen: false,
  counterKitchen: false,
  gasStove: false,
  ihCooktop: false,
  dishwasher: false,
  // Building
  buildingAge: "any",
  floor: "any",
  buildingStructure: [],
  elevator: false,
  southFacing: false,
  cornerUnit: false,
  balcony: false,
  flooringWood: false,
  // Security
  autoLock: false,
  securityCamera: false,
  videoIntercom: false,
  deliveryBox: false,
  // Amenities
  airConditioning: false,
  internetIncluded: false,
  parking: false,
  bicycleParking: false,
  motorcycleParking: false,
  // Access
  walkToStation: "any",
  // Contract
  foreignerFriendly: false,
  petsAllowed: false,
  instrumentsOK: false,
  sohoOK: false,
  noGuarantorNeeded: false,
  moveInWithin30Days: false,
  // Listing
  listingFreshness: "any",
};
